import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, ChevronsDown, Frown, GripVertical, Smile, SquarePen } from 'lucide-react';
import { Button } from '../../../shared/components/Button';
import '../../../shared/styles/Orchestrator.css';

function TokenList({
  tokens,
  selectedTokenId = null,
  onToggleVisible,
  onToggleDeath,
  onToggleDisposition,
  onEdit,
  onToggleGroupVisible,
  onReorder,
}) {
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const rowRefs = useRef(new Map());

  const toggleCollapse = (grp) => {
    setCollapsedGroups((prev) => ({ ...prev, [grp]: !prev[grp] }));
  };

  const grouped = {};
  const ungrouped = [];
  tokens.forEach((token) => {
    const grp = (token.group || '').trim();
    if (grp) {
      if (!grouped[grp]) grouped[grp] = [];
      grouped[grp].push(token);
    } else {
      ungrouped.push(token);
    }
  });

  const clearDrag = () => { setDragId(null); setDragOverId(null); };

  const selectedToken = useMemo(
    () => tokens.find((token) => token.id === selectedTokenId) || null,
    [tokens, selectedTokenId]
  );

  useEffect(() => {
    if (!selectedToken) return;
    const selectedGroup = (selectedToken.group || '').trim();
    if (!selectedGroup) return;
    if (collapsedGroups[selectedGroup]) {
      setCollapsedGroups((prev) => ({ ...prev, [selectedGroup]: false }));
    }
  }, [selectedToken, collapsedGroups]);

  useEffect(() => {
    if (!selectedTokenId) return;
    const rowEl = rowRefs.current.get(selectedTokenId);
    if (!rowEl) return;
    const scroller = rowEl.closest('.orchestrator_scrollable_content') || rowEl.closest('.orchestrate_panel');
    if (!scroller) return;

    const rowRect = rowEl.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    const targetTop =
      scroller.scrollTop +
      (rowRect.top - scrollerRect.top) -
      (scroller.clientHeight / 2 - rowRect.height / 2);

    scroller.scrollTo({
      top: Math.max(0, targetTop),
      behavior: 'smooth',
    });
  }, [selectedTokenId, collapsedGroups]);

  const handleDrop = (targetItemId, targetGroupName) => {
    if (!dragId || !onReorder) { clearDrag(); return; }
    clearDrag();
    if (dragId === targetItemId) return;
    const allItems = [...Object.values(grouped).flat(), ...ungrouped];
    const dragged = allItems.find((t) => t.id === dragId);
    if (!dragged) return;
    const rest = allItems.filter((t) => t.id !== dragId);
    const moved = { ...dragged, group: targetGroupName };
    if (targetItemId != null) {
      const idx = rest.findIndex((t) => t.id === targetItemId);
      rest.splice(idx >= 0 ? idx : rest.length, 0, moved);
    } else {
      const grpItems = rest.filter((t) => (t.group || '').trim() === targetGroupName);
      if (grpItems.length > 0) {
        const lastIdx = rest.findIndex((t) => t.id === grpItems[grpItems.length - 1].id);
        rest.splice(lastIdx + 1, 0, moved);
      } else {
        rest.push(moved);
      }
    }
    onReorder(rest);
  };

  const renderToken = (token) => (
    <div
      key={token.id}
      className={`token_row${dragId === token.id ? ' drag_dragging' : ''}${dragOverId === token.id ? ' drag_over_before' : ''}${selectedTokenId === token.id ? ' token_row_selected' : ''}`}
      ref={(node) => {
        if (node) {
          rowRefs.current.set(token.id, node);
        } else {
          rowRefs.current.delete(token.id);
        }
      }}
      draggable
      onDragStart={(e) => { e.stopPropagation(); setDragId(token.id); setDragOverId(null); }}
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverId(token.id); }}
      onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleDrop(token.id, (token.group || '').trim()); }}
      onDragEnd={clearDrag}
    >
      <span className='drag_handle'><GripVertical size={14} /></span>
      <span className='token_name'>{token.name}</span>
      <div className='token_buttons'>
        <Button
          icon={token.visible === false ? 'show' : 'hide'}
          type={token.visible === false ? 'black-white' : 'tomato'}
          condensed
          description={token.visible === false ? 'Show' : 'Hide'}
          onPress={() => onToggleVisible(token.id)}
        />
        <Button
          icon={token.grayscale ? 'removeDeath' : 'death'}
          type={token.grayscale ? 'tomato' : 'black-white'}
          condensed
          description={token.grayscale ? 'Mark as Alive' : 'Mark as Dead'}
          onPress={() => onToggleDeath(token.id)}
        />
        {token.goodImageId && token.evilImageId && (
          <Button
            icon={token.disposition === 'evil' ? Smile : Frown}
            type={token.disposition === 'evil' ? 'tomato' : 'black-white'}
            condensed
            description={token.disposition === 'evil' ? 'Switch to Good' : 'Switch to Evil'}
            onPress={() => onToggleDisposition(token.id)}
          />
        )}
        <Button
          icon={SquarePen}
          type='black-white'
          condensed
          description='Edit'
          onPress={() => onEdit(token)}
        />
      </div>
    </div>
  );

  return (
    <div className='token_list'>
      {!tokens.length && <div className='no_entity'>No tokens yet.</div>}
      {Object.entries(grouped).map(([grp, grpTokens]) => {
        const isCollapsed = !!collapsedGroups[grp];
        const allVisible = grpTokens.every((t) => t.visible !== false);
        const isGrpOver = dragOverId === `__grp_${grp}`;
        const hasSelected = selectedToken && (selectedToken.group || '').trim() === grp;
        const CollapseIcon = hasSelected ? ChevronsDown : (isCollapsed ? ChevronRight : ChevronDown);
        return (
          <div key={grp} className='group_section' style={{marginBottom: '-1px'}}>
            <div
              className={`group_header${isGrpOver ? ' drag_over_group' : ''}`}
              onClick={() => { if (!hasSelected) toggleCollapse(grp); }}
              role='button'
              tabIndex={0}
              aria-expanded={!isCollapsed}
              onKeyDown={(e) => {
                if (e.key === ' ') e.preventDefault();
                if ((e.key === 'Enter' || e.key === ' ') && !hasSelected) toggleCollapse(grp);
              }}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverId(`__grp_${grp}`); }}
              onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleDrop(null, grp); }}
            >
              <button className='group_collapse_btn' tabIndex={-1} aria-hidden='true'>
                <CollapseIcon size={14} />
              </button>
              <span className='group_name' title={grp}>{grp}</span>
              <div onClick={(e) => e.stopPropagation()}>
                <Button
                  icon={allVisible ? 'hide' : 'show'}
                  type={allVisible ? 'tomato' : 'black-white'}
                  condensed
                  description={allVisible ? 'Hide Group' : 'Show Group'}
                  onPress={() => onToggleGroupVisible(grp)}
                />
              </div>
            </div>
            {!isCollapsed && (
              <div
                className='group_items'
                onDragOver={(e) => { e.preventDefault(); }}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleDrop(null, grp); }}
              >
                {grpTokens.map(renderToken)}
              </div>
            )}
          </div>
        );
      })}
      <div
        className='ungrouped_zone_token'
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleDrop(null, ''); }}
      >
        {ungrouped.map(renderToken)}
      </div>
    </div>
  );
}

export default TokenList;
